// Descarga del documento al cliente del Pricer Datec (F4 · T8): `GET /api/pricer-datec/quotes/:id/documento.pdf`.
//
// La usa también el espejo (`/quotes/:id`, al que se llega desde Aprobaciones): el PDF de `/api/pdf` ya no sirve
// cotizaciones del Pricer (da 404), así que el espejo busca la cotización del Pricer por su código y pide el
// documento nuevo. El servidor decide los permisos: quien no la ve, recibe un 404 y acá se le dice en llano.
import { axiosInstance } from '@/lib/api';
import { nombreDelAdjunto } from '@/features/smart-pricing/services/corridas-api';
import { pricerDatecService } from './pricer-datec.service';

/** Un código del Pricer lo pone el backend (`generateCode`): `PD-AAAA-NNNN`. */
export const esCodigoDelPricer = (codigo?: string | null): boolean => !!codigo?.startsWith('PD-');

/**
 * El mensaje que mandó el servidor. Con `responseType: 'blob'` o `'arraybuffer'` el cuerpo del error llega
 * como bytes: hay que decodificarlo, si no la persona lee «Request failed with status code 409».
 */
export async function mensajeDelServidor(error: unknown, porOmision: string): Promise<string> {
  const respuesta = (error as { response?: { data?: unknown } } | null)?.response;
  const datos = respuesta?.data;
  try {
    let texto: string | null = null;
    if (typeof Blob !== 'undefined' && datos instanceof Blob)
      texto = new TextDecoder().decode(await datos.arrayBuffer());
    else if (Object.prototype.toString.call(datos) === '[object ArrayBuffer]')
      texto = new TextDecoder().decode(datos as ArrayBuffer);
    else if (typeof datos === 'string') texto = datos;
    else if (datos && typeof datos === 'object') {
      const o = datos as { error?: unknown; message?: unknown };
      const m = o.error ?? o.message;
      if (typeof m === 'string' && m) return m;
    }
    if (texto) {
      const cuerpo = JSON.parse(texto) as { error?: unknown; message?: unknown };
      const m = cuerpo.error ?? cuerpo.message;
      if (typeof m === 'string' && m) return m;
    }
  } catch {
    // No era JSON: se usa el mensaje por omisión.
  }
  return porOmision;
}

export interface DocumentoDescargado {
  blob: Blob;
  nombre: string;
}

/** Baja el PDF de una cotización del Pricer por su id. Si falla, lanza `Error` con el mensaje del servidor. */
export async function descargarDocumentoPdf(
  quoteId: string,
  nombrePorOmision = 'cotizacion.pdf'
): Promise<DocumentoDescargado> {
  try {
    const res = await axiosInstance.get(`/api/pricer-datec/quotes/${quoteId}/documento.pdf`, {
      responseType: 'blob',
    });
    return {
      blob: new Blob([res.data], { type: 'application/pdf' }),
      nombre: nombreDelAdjunto(res.headers?.['content-disposition'], nombrePorOmision),
    };
  } catch (e) {
    throw new Error(
      await mensajeDelServidor(
        e,
        'No se pudo generar el PDF. Probá de nuevo; si sigue, avisá a soporte.'
      )
    );
  }
}

/**
 * Desde el espejo (el `Quote` de Aprobaciones). El prefijo `PD-` sólo decide si vale la pena preguntar; la verdad
 * la da el servidor por el VÍNCULO real (una consulta puntual, no la lista de cotizaciones del Pricer):
 * - devuelve `null` si no es espejo (una cotización normal con prefijo `PD`): que siga por `/api/pdf`.
 * - lanza «No tenés acceso…» si es espejo y la persona no la ve (404 del servidor).
 */
export async function descargarDocumentoDelEspejo(
  quoteId: string,
  codigo: string
): Promise<DocumentoDescargado | null> {
  let ficha: { id: string; code: string } | null;
  try {
    ficha = await pricerDatecService.resolverEspejo(quoteId);
  } catch (e) {
    const estado = (e as { response?: { status?: number } } | null)?.response?.status;
    if (estado === 404) {
      throw new Error(
        'No tenés acceso al documento de esta cotización. Pedile acceso a quien la creó.'
      );
    }
    throw new Error(
      await mensajeDelServidor(e, 'No se pudo preparar la descarga. Probá de nuevo.')
    );
  }
  if (!ficha) return null;
  return descargarDocumentoPdf(ficha.id, `${codigo}.pdf`);
}

/** Dispara la descarga en el navegador. */
export function guardarArchivo({ blob, nombre }: DocumentoDescargado): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}
